import { z } from 'zod';
import { ResourceLibraryInternalLinkItem } from '../types/resourceLibraryItems';
import { GetResourceLibraryInternalLinkItemListingSchema } from '../schemas/resourceLibraryInternalLinkItemZodSchemas';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';

/**
 * Transforms and validates the response from the getResourceLibraryInternalLinkItemListing GraphQL query
 * to a simplified ResourceLibraryItem type.
 *
 * @param data The response data from the getResourceLibraryInternalLinkItemListing query
 * @returns An array of valid ResourceLibraryItem objects
 */
export function transformResourceLibraryInternalLinkItemsResponse(
    data: unknown // GetResourceLibraryInternalLinkItemListingQuery - use unknown for jest tests be able to
    // pass invalid data
): ResourceLibraryInternalLinkItem[] {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetResourceLibraryInternalLinkItemListingSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibraryItemListing:',
            parsedResult.error.errors
        );
        return [];
    }

    const resourceItemListingEdges =
        parsedResult.data.getResourceLibraryInternalLinkItemListing.edges;

    // Transform resource items to simplified ResourceLibraryItem type
    return resourceItemListingEdges.map(({ node }) => {
        const item = node as z.infer<
            typeof GetResourceLibraryInternalLinkItemListingSchema
        >['getResourceLibraryInternalLinkItemListing']['edges'][number]['node'];

        const internalLinkItem: ResourceLibraryInternalLinkItem = {
            kind: 'internalLink',
            id: item.id,
            key: item.key,
            name: item.name,
            description: item.description ?? '', // Description is nullable
            brandId: item.brand ? item.brand.id : null,
            productGroupIds: item.productGroup
                ? item.productGroup.map((pg: z.infer<typeof ProductGroupSchema>) => pg.id)
                : [],
            productApplicationIds: item.productApplication
                ? item.productApplication.map(
                      (pa: z.infer<typeof ProductApplicationSchema>) => pa.id
                  )
                : [],
            resourceTypeId: item.resourceType.id,
            navigationScopeIds: item.navigationScope
                ? item.navigationScope.map((ns: z.infer<typeof NavigationNodeSchema>) => ns.id)
                : [],
            exclusiveNavigationScopeIds: item.exclusiveNavigationScope
                ? item.exclusiveNavigationScope.map(
                      (ens: z.infer<typeof NavigationNodeSchema>) => ens.id
                  )
                : [],
            publicationDate: item.publicationDate,
            thumbnailUrl: item.thumbnailUrl,
            route: item.route,
        };

        return internalLinkItem;
    });
}
